import React from "react";
import { Button } from "@shared/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/ui/select";
import { Label } from "@shared/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { DownloadIcon, InfoIcon, FileTextIcon, Loader2 } from "lucide-react";

interface Step1Props {
  selectedEntity: string;
  setSelectedEntity: (value: string) => void;
  entityTypeOptions: Array<{ value: any; label: any; id: any }>;
  handleDownloadTemplate: () => void;
  isDownloading: boolean;
  hasDownloaded: boolean;
  setStep: (step: number) => void;
}

export const Step1Component: React.FC<Step1Props> = ({
  selectedEntity,
  setSelectedEntity,
  entityTypeOptions,
  handleDownloadTemplate,
  isDownloading,
  hasDownloaded,
  setStep,
}) => {
  const selectedEntityData = entityTypeOptions.find((e) => e.value === selectedEntity);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Step 1: Select Entity Type & Download Template
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="entityType" className="flex items-center gap-2">
              Select Entity Type <span className="text-red-500">*</span>
              <InfoIcon className="w-4 h-4 text-gray-400" />
            </Label>
            <Select value={selectedEntity} onValueChange={setSelectedEntity}>
              <SelectTrigger>
                <SelectValue placeholder="Choose entity type to import" />
              </SelectTrigger>
              <SelectContent>
                {entityTypeOptions.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              Select the type of entities you want to import in bulk
            </p>
          </div>
          {selectedEntityData && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <FileTextIcon className="w-4 h-4" />
                Template for {selectedEntityData.label}:
              </h4>
              <div className="text-sm space-y-1">
                <p>
                  <strong>Required fields:</strong> parent_name, entity_name, status
                </p>
                <p>
                  <strong>Optional fields:</strong>description, manager
                </p>
                <p className="text-xs text-gray-600 mt-2">
                  Download the template and fill it with your data. Make sure to follow
                  the format exactly.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <div className="flex justify-between">
        <Button
          onClick={handleDownloadTemplate}
          disabled={!selectedEntity || isDownloading}
          className="bg-green-600 hover:bg-green-700"
        >
          {isDownloading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Downloading...
            </>
          ) : (
            <>
              <DownloadIcon className="w-4 h-4 mr-2" />
              Download Template
            </>
          )}
        </Button>
        <Button onClick={() => setStep(2)}>
          Next: Upload File
        </Button>
      </div>
    </div>
  );
};